import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import { useIntl } from 'react-intl'
import { Pressable } from 'react-native'
import { connect, ConnectedProps, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'

import { skipToken } from '@reduxjs/toolkit/query'
import { isAfter, sub } from 'date-fns'
import { get, isEmpty, isNil, noop } from 'lodash-es'
import { bindActionCreators } from 'redux'
import styled, { useTheme } from 'styled-components/native'
import { v4 as uuidv4 } from 'uuid'

import { usePrevious } from '@lyrahealth-inc/shared-app-logic/src/features/common/hooks/usePrevious'
import { parseAllowedMessages } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/messageUtils'
import { isFileNameAlreadyExistsError } from '@lyrahealth-inc/shared-app-logic/src/features/documents/documentErrorUtils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { isV2AiConversation } from '@lyrahealth-inc/shared-app-logic/src/features/messaging/conversations'
import {
  getCoveringProviderActorName,
  getMessageAuthorAttribution,
} from '@lyrahealth-inc/shared-app-logic/src/features/messaging/coveringProviderAttribution'
import { isAiConversationType } from '@lyrahealth-inc/shared-app-logic/src/features/messaging/utils'
import { Programs } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programConfigs'
import { canSeeDirectBookingLink } from '@lyrahealth-inc/shared-app-logic/src/features/providers/utils'
import { convertHtmlSpace, parseHtmlTags } from '@lyrahealth-inc/shared-app-logic/src/features/selfCareMedia/utils'
import { isDateOfBirthUnder18 } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/utils'
import { Draft } from '@lyrahealth-inc/shared-app-logic/src/models/draft/Draft'
import { Episode, EpisodeStates } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { Feedback, FeedbackEntityType } from '@lyrahealth-inc/shared-app-logic/src/models/feedback/Feedback'
import {
  CONVERSATION_TYPE,
  LyraLibraryResource,
  MESSAGE_AUTHOR_TYPE,
  MessageAttributes,
} from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'
import { parseEssentialsMetadata } from '@lyrahealth-inc/shared-app-logic/src/models/messages/parseEssentialsMetadata'
import { ProviderInfo } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { CloseCircleIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/CloseCircleIcon'
import { InboxIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/InboxIcon'
import { PencilSparkleIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/PencilSparkleIcon'
import { PlusSignIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/PlusSignIcon'
import { ResourcesIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ResourcesIcon'
import { RulerAndPencilIconFilled } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/RulerAndPencilIconFilled'
import { SideNavDocumentIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/SideNavDocumentIcon'
import { InfoPopover } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/infoPopover/InfoPopover'
import { PressableOpacity } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/pressableOpacity/PressableOpacity'
import { DirectBookingLinkInline } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/directBookingLink/DirectBookingLinkInline'
import { DirectBookingLinkMessageAttachment } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/directBookingLinkMessageAttachment/DirectBookingLinkMessageAttachment'
import { EssentialsLinkMessageAttachment } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/essentialsLinkMessageAttachment/EssentialsLinkMessageAttachment'
import { CloseStyles } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/chatHeader/ChatHeader'
import { ChatSideBarSelection } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/chatRightSideBar/ChatRightSideBar'
import { ConfirmationModal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/confirmationModal/ConfirmationModal'
import { Chat, ChatHandle } from '@lyrahealth-inc/ui-core-crossplatform/src/pages/chat/Chat'
import { ActivityReviewInfo } from '@lyrahealth-inc/ui-core-crossplatform/src/ui-models/assignments/Assignments'
import {
  MessageAttachmentData,
  MessageAttachmentType,
  MessageType,
} from '@lyrahealth-inc/ui-core-crossplatform/src/ui-models/messages/Messages'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { ESSENTIALS_ACCESS } from './data/essentialsAccess'
import { useEssentialsAccess } from './data/hooks/useEssentialsAccess'
import { useInfiniteMessages } from './data/hooks/useInfiniteMessages'
import { useSaveMessageDraft } from './data/hooks/useSaveMessageDraft'
import { useSelectedConversation } from './data/hooks/useSelectedConversation'
import messagesApi, {
  getReplacementConversationId,
  useDeleteDraftMutation,
  useLazyGetMessageQuery,
  useMarkProviderPairReadMutation,
  useSendMessageMutation,
  useUpdateConversationAttributesMutation,
} from './data/messagesApi'
import {
  getConversationsClient,
  getEssentialsArticleUrl,
  getMessengerDrafts,
  getSelectedConversationId,
  getUnsentMessage,
  inLiveMsgSession,
} from './data/messagesSelectors'
import {
  saveMessageDraft,
  setEssentialsArticleUrl,
  setInsertEssentialsArticleForClientId,
  setUnsentMessage,
  toggleLiveMsgSession,
} from './data/messagesSlice'
import { getAiConversationMixpanelProperties } from './data/mixpanelUtils'
import {
  doesConversationMatchRecipient,
  hasErrorSpanPattern,
  hasRecipientMessages,
  isMessageFromRecipient,
} from './data/utils'
import useMarkMessagesUnread from './hooks/useMarkMessagesUnread'
import { SupervisorMessageAttributionNotice } from './messenger/SupervisorMessageAttributionNotice'
import { mixpanelEvents } from '../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../mixpanel/mixpanelTracking'
import { ESSENTIALS_BASE_URL, SUPERVISOR_ROLES } from '../../common/constants/appConstants'
import { CLIENT_DOCUMENTS, CLIENTS_ASSIGNMENT_DETAILS, CLIENTS_SESSIONS } from '../../common/constants/routingConstants'
import { getClientFullName, hasRole } from '../../common/utils/utils'
import { getAuthSupervisor, getAuthUser, getAuthUserId } from '../../data/auth/authSelectors'
import { getSelectedClientId } from '../../data/lyraTherapy/clientSelectors'
import { RootState, useAppDispatch } from '../../data/store'
import { setLastConversationClientId } from '../../data/userSettingsSlice'
import { useGuardianForSelectedClient } from '../../hooks/useGuardianForSelectedClient'
import { useIsEngageDAProvider } from '../../hooks/useIsEngageDAProvider'
import { clearSelectedAssignmentId } from '../assignments/data/assignmentsSlice'
import { useCreateDocumentFromTemplateMutation, useShareDocumentMutation } from '../clientDocuments/data/documentsApi'
import {
  useCreateFeedbackMutation,
  useDeleteFeedbackMutation,
  useGetAiCoachFeedbackQuery,
  useUpdateFeedbackResponseMutation,
} from '../clientNotes/data/notesApi'
import { useClientAppointmentsData } from '../clients/clientDetails/data/hooks/useClientAppointmentsData'
import { useSelectedClientData } from '../clients/clientDetails/data/hooks/useSelectedClientData'
import { setSelectedClientId } from '../clients/clientDetails/data/ltClientSlice'
import { setToastContent } from '../data/ltToastAutoActions'
import { Template } from '../documents/data/templatesApi'
import { useEpisodesForSelectedClient } from '../episodes/data/hooks/useEpisodesForSelectedClient'
import { useSelectedEpisodeForClient } from '../episodes/data/hooks/useSelectedEpisodeForClient'
import { useDraftsData } from '../messages/data/hooks/useDraftsData'

const InboxIconContainer = styled(PressableOpacity)(({ theme }) => ({
  marginLeft: theme.spacing['12px'],
  // Negative margin to align icon with the text without changing text formatting
  marginTop: '-4px',
  display: 'flex',
  padding: theme.spacing['8px'],
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing['8px'],
  borderRadius: '100px',
  backgroundColor: theme.colors.backgroundIcon,
}))

const QuickActionsMenuItem = styled(PressableOpacity)<{ disabled?: boolean }>(({ theme, disabled }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  paddingVertical: theme.spacing['12px'],
  paddingHorizontal: theme.spacing['16px'],
  gap: theme.spacing['8px'],
  opacity: disabled ? 0.4 : 1,
}))

const DocumentAttachmentRow = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  backgroundColor: theme.colors.backgroundSecondary,
  borderRadius: '16px',
  border: `1px solid ${theme.colors.borderDefault}`,
  padding: theme.spacing['16px'],
  marginBottom: theme.spacing['16px'],
  gap: theme.spacing['8px'],
}))

const DocumentAttachmentFileName = styled(BodyText)({
  flex: 1,
})

const messageReducer = (state: any, action: any) => {
  switch (action.type) {
    case 'ON_TYPING': {
      return { ...state, receiverTyping: action.receiverTyping }
    }
    case 'NEW_MESSAGE': {
      return { ...state, newMessageCount: state.newMessageCount + 1, receiverTyping: false }
    }
    case 'READ_MESSAGE': {
      return { ...state, newMessageCount: 0 }
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`)
    }
  }
}

type BaseChatProps = ConnectedProps<typeof connector> & {
  closePopover?: () => void
  isLoading?: boolean
  onMessageSent?: () => void
  onChatHandleChange?: (handle: ChatHandle | null) => void
  entryPoint?: ChatEntryPoint
  selectedEpisodeId?: string
  savedTemplateId?: string
  setSavedTemplateId?: (id: string | null) => void
  onOpenDocumentLibrary?: () => void
  onOpenSideBarItem?: (selection: ChatSideBarSelection) => void
  showInputActions?: boolean
  recipientName?: string
  recipientId?: string
  disableDrafts?: boolean
  ensureConversation?: () => Promise<string | null>
  composerNotice?: React.ReactNode
  isProviderPairReadOnly?: boolean
  isProviderPair?: boolean
} & Partial<React.ComponentProps<typeof Chat>>

export enum ChatEntryPoint {
  MESSAGES_TAB = 'Messages Tab',
  TO_DO_LIST = 'To Do List',
  CLIENT_HOME = 'Client Home',
  CLIENT_SESSIONS = 'Client Sessions',
  CLIENT_PROGRESS = 'Client Progress',
  CLIENT_NOTES = 'Client Notes',
  CLIENT_MEDICATIONS = 'Client Medications',
}

export type InsertMessageOptions = {
  onSend?: () => void
  attributesToAttach?: Record<string, any>
}
export type BaseChatHandle = {
  get message(): string
  takeMessageForTransfer: () => string
  insertMessage: (message: string, opts?: InsertMessageOptions) => void
  insertDirectBookingLink: () => void
  insertEssentialsLinkUrl: () => void
  attachDocument: (doc: Template) => void
  get isReady(): boolean
}

export const BaseChat = forwardRef<BaseChatHandle, BaseChatProps>(
  (
    {
      draft,
      conversationsClient,
      closePopover,
      inLiveMsgSession,
      isLoading = false,
      onMessageSent,
      entryPoint,
      selectedClientId,
      selectedEpisodeId,
      onChatHandleChange,
      savedTemplateId,
      setSavedTemplateId,
      onOpenDocumentLibrary,
      onOpenSideBarItem,
      showInputActions = true,
      recipientName,
      recipientId,
      disableDrafts = false,
      ensureConversation,
      composerNotice,
      isProviderPairReadOnly = false,
      isProviderPair = false,
      ...props
    },
    ref,
  ) => {
    const {
      isPreferredNameEnabled,
      supervisorsThatCanMarkConversationsAsRead,
      shouldShowMessengerDraftsUtility,
      isDocumentMessageEnabled,
      showTemplatesButtonInMessageCenter,
      shouldShowMessengerAIDrafts,
      shouldShowPostSessionAIDrafts,
      showWellnessCenterEssentials,
      isSupervisorMessageAttributionEnabled,
    } = useFlags()
    const isEssentialsDisabled = useEssentialsAccess() === ESSENTIALS_ACCESS.DISABLED
    const { colors } = useTheme()
    const intl = useIntl()
    const { formatMessage } = intl
    const conversationInstanceId = useRef(uuidv4())
    const trackedConversationIdRef = useRef<string | null>(null)
    const sendInFlightRef = useRef(false)
    const providerId = useSelector(getAuthUserId)
    const supervisor = useSelector(getAuthSupervisor)
    const coveringProviderActorName = getCoveringProviderActorName(supervisor, providerId)
    const provider = useSelector(getAuthUser)
    const selectedConversation = useSelectedConversation()
    const [initialValue, setInitialValue] = useState('')
    const [state, dispatch] = useReducer(messageReducer, {
      receiverTyping: false,
      newMessageCount: 0,
    })
    const reduxDispatch = useAppDispatch()
    const { data: appointments } = useClientAppointmentsData()
    const { data: client } = useSelectedClientData()
    const { data: episodes } = useEpisodesForSelectedClient()
    const isDAEngageProvider = useIsEngageDAProvider()
    const {
      data: messagesData,
      isLoading: loadingMessages,
      isSuccess: messagesFetched,
      loadMore: loadMoreMessages,
      hasMore: hasMoreMessages,
      isLoadingNext: isLoadingNextPage,
    } = useInfiniteMessages()
    const isAiConversationSelected = isAiConversationType(selectedConversation?.conversation_attributes?.type)
    const isV2AiCoachSelected = isV2AiConversation(selectedConversation)
    const isProviderPairConversation =
      selectedConversation?.conversation_attributes?.type === CONVERSATION_TYPE.PROVIDER_PROVIDER

    const { data: aiCoachFeedback } = useGetAiCoachFeedbackQuery(
      isAiConversationSelected ? { providerId, entityType: FeedbackEntityType.AI_COACH } : skipToken,
    )
    const [createFeedback] = useCreateFeedbackMutation()
    const [deleteFeedbackMutation] = useDeleteFeedbackMutation()
    const [updateFeedbackResponseMutation] = useUpdateFeedbackResponseMutation()

    const messengerDraftsEnabled = shouldShowMessengerDraftsUtility && !disableDrafts

    const { data: conversationDrafts } = useDraftsData(undefined, {
      skip: !messengerDraftsEnabled,
    })

    const messages = useMemo(() => (selectedConversation ? messagesData : []), [selectedConversation, messagesData])

    const [getMessage] = useLazyGetMessageQuery()
    const [createMessage] = useSendMessageMutation()
    const [updateConversationAttributes] = useUpdateConversationAttributesMutation()
    const [markProviderPairRead] = useMarkProviderPairReadMutation()
    const [deleteDraft] = useDeleteDraftMutation()
    const [createDocumentFromTemplate] = useCreateDocumentFromTemplateMutation()
    const [shareDocument] = useShareDocumentMutation()
    const appDispatch = useAppDispatch()
    const insertMessageOptions = useRef<InsertMessageOptions | null>(null)
    const prevSelectedClientId = usePrevious(selectedClientId)

    const { data: currentEpisode } = useSelectedEpisodeForClient(selectedEpisodeId) as {
      data: Episode | undefined
    }
    const selectedEpisodeProgram = currentEpisode?.program_name

    // Guardian messaging always targets the client's active episode (product requirement), matching the
    // popover thread list / header. Resolve against the active episode, not the viewed (selected) one.
    const { guardian, isGuardianForMinor } = useGuardianForSelectedClient()

    const isSupervisor = supervisor && !isEmpty(supervisor.roles) && hasRole(supervisor.roles, SUPERVISOR_ROLES)
    const { receiverTyping, newMessageCount } = state
    // The open-time read checks the count without re-running for each new message.
    const newMessageCountRef = useRef(newMessageCount)
    newMessageCountRef.current = newMessageCount
    const [addedDBLAttachment, setAddedDBLAttachment] = useState(false)
    const [addedEssentialsLink, setAddedEssentialsLink] = useState(false)
    const [insertedEssentialsLinkUrl, setInsertedEssentialsLinkUrl] = useState<string | null>(null)
    const [attachedDocument, setAttachedDocument] = useState<Template | null>(null)
    const inputRef = useRef('')
    const conversationDraftRef = useRef<Draft | undefined>(undefined)
    const dblAttachmentRef = useRef(false)
    const essentialsLinkRef = useRef(false)
    const essentialsLinkUrlRef = useRef<string | null>(null)
    // Booking and Essentials links reach Message center only through the draft saved on unmount.
    const savesDraftWithLinksRef = useRef(false)

    // The attachments a provider can add to the message being composed. `keepArticlePreview` leaves the
    // Essentials article open in the side bar (its preview state) while dropping it from the composer.
    const clearComposerAttachments = useCallback(
      ({ keepArticlePreview = false }: { keepArticlePreview?: boolean } = {}) => {
        essentialsLinkUrlRef.current = null
        setAddedDBLAttachment(false)
        setAddedEssentialsLink(false)
        setInsertedEssentialsLinkUrl(null)
        setAttachedDocument(null)
        if (!keepArticlePreview) {
          appDispatch(setEssentialsArticleUrl(null))
        }
        appDispatch(setInsertEssentialsArticleForClientId(null))
      },
      [appDispatch],
    )

    // Insert options and attachments belong to the client they were added for. Keyed on the client, not the
    // conversation: the selected conversation is briefly null while the new client's conversations load.
    // Saved drafts are reloaded by the drafts effect below. Only a direct client-to-client switch resets, so
    // the compose flow (attach, clear the client, pick one) keeps what was attached.
    useEffect(() => {
      if (prevSelectedClientId && selectedClientId && selectedClientId !== prevSelectedClientId) {
        insertMessageOptions.current = null
        setSavedTemplateId?.(null)
        clearComposerAttachments({ keepArticlePreview: true })
      }
    }, [clearComposerAttachments, prevSelectedClientId, selectedClientId, setSavedTemplateId])
    const chatRef = useRef<ChatHandle | null>(null)
    const handleChatRef = useCallback(
      (handle: ChatHandle | null) => {
        chatRef.current = handle
        onChatHandleChange?.(handle)
      },
      [onChatHandleChange],
    )
    const [inputSelection, setInputSelection] = useState<{ start: number; end: number } | undefined>(undefined)
    const [showContainsErrorModal, setShowContainsErrorModal] = useState(false)
    const [isPlusMenuOpen, setIsPlusMenuOpen] = useState(false)
    const [showDocumentDuplicateModal, setShowDocumentDuplicateModal] = useState(false)
    const [showDocumentGenericErrorModal, setShowDocumentGenericErrorModal] = useState(false)
    const pendingMessageValue = useRef('')
    // Set by a send that created the conversation, until the composer shows that conversation.
    const createdConversationIdRef = useRef<string | null>(null)
    useEffect(() => {
      if (!selectedConversation?.conversation_id) {
        return
      }
      if (createdConversationIdRef.current) {
        if (selectedConversation.conversation_id === createdConversationIdRef.current) {
          // The new conversation's first history load can remount the input from initialValue, so
          // carry over anything typed since the send.
          if (inputRef.current) {
            setInitialValue(inputRef.current)
          }
        } else {
          // A different conversation arrived first (the provider switched threads). The cleanups
          // kept the text for the created one, so clear it here as a switch normally would. The
          // input holds its own value, and initialValue is often already empty, so reset it directly.
          inputRef.current = ''
          setInitialValue('')
          chatRef.current?.insertMessage('')
        }
      }
      createdConversationIdRef.current = null
    }, [selectedConversation?.conversation_id])

    // Block sends initiated after the selected provider-pair thread becomes read-only.
    const isReadOnlyRef = useRef(false)
    isReadOnlyRef.current = isProviderPairReadOnly

    useImperativeHandle(
      ref,
      () => ({
        insertMessage: (message, opts) => {
          inputRef.current = message
          insertMessageOptions.current = opts ?? null
          chatRef.current?.insertMessage(message)
        },
        insertDirectBookingLink: () => {
          setAddedDBLAttachment(true)
        },
        insertEssentialsLinkUrl: () => {
          setAddedEssentialsLink(true)
          setInsertedEssentialsLinkUrl(essentialsLinkUrlRef.current)
        },
        attachDocument: (doc: Template) => {
          setAttachedDocument(doc)
        },
        takeMessageForTransfer: () => {
          // Leave text with a link in place so the saved draft carries both.
          if ((dblAttachmentRef.current || essentialsLinkRef.current) && savesDraftWithLinksRef.current) {
            return ''
          }
          const message = inputRef.current
          // The destination composer owns this text; do not save another draft on unmount.
          inputRef.current = ''
          return message
        },
        get message() {
          return inputRef.current
        },
        get isReady() {
          return chatRef.current?.isReady ?? false
        },
      }),
      [],
    )

    useEffect(() => {
      const conversationType = selectedConversation?.conversation_attributes?.type
      const conversationId = selectedConversation?.conversation_id
      if (!entryPoint || !conversationId) {
        return
      }
      if (selectedClientId && conversationType && trackedConversationIdRef.current !== conversationId) {
        trackedConversationIdRef.current = conversationId
        track({
          event: mixpanelEvents.OPEN_MESSENGER,
          properties: {
            entry_point: entryPoint,
            ...getAiConversationMixpanelProperties(conversationType),
          },
          details: {
            program: currentEpisode?.program_name,
            clientLyraId: selectedClientId,
            conversationType,
          },
        })
      }
    }, [
      currentEpisode?.program_name,
      entryPoint,
      selectedClientId,
      selectedConversation?.conversation_attributes?.type,
      selectedConversation?.conversation_id,
    ])

    useEffect(() => {
      if (!shouldShowMessengerDraftsUtility) {
        return
      }
      if (!selectedClientId || !selectedConversation?.conversation_id) {
        conversationDraftRef.current = undefined
        inputRef.current = ''
        setInitialValue('')
        dblAttachmentRef.current = false
        essentialsLinkRef.current = false
        setAddedDBLAttachment(false)
        return
      }
      if (disableDrafts) {
        conversationDraftRef.current = undefined
        return
      }
      conversationDraftRef.current =
        selectedClientId && selectedConversation?.conversation_id
          ? conversationDrafts?.find(
              (draft) =>
                draft.conversation_id === selectedConversation?.conversation_id &&
                draft.meta_data?.patient_lyra_id === selectedClientId,
            )
          : undefined

      if (!inputRef.current) {
        const body = conversationDraftRef.current?.body ?? ''
        setInitialValue(body)
        setInputSelection(body ? { start: body.length, end: body.length } : undefined)
        dblAttachmentRef.current = conversationDraftRef.current?.meta_data?.dbl_attached ?? false
        essentialsLinkRef.current = conversationDraftRef.current?.meta_data?.essentials_link_attached ?? false
        essentialsLinkUrlRef.current = conversationDraftRef.current?.meta_data?.essentials_link_url ?? null
        setAddedDBLAttachment(conversationDraftRef.current?.meta_data?.dbl_attached ?? false)
        setAddedEssentialsLink(conversationDraftRef.current?.meta_data?.essentials_link_attached ?? false)
        setInsertedEssentialsLinkUrl(conversationDraftRef.current?.meta_data?.essentials_link_url ?? null)
        if (conversationDraftRef.current?.meta_data?.essentials_link_url) {
          appDispatch(setEssentialsArticleUrl(conversationDraftRef.current.meta_data.essentials_link_url))
        }
      }
    }, [
      appDispatch,
      conversationDrafts,
      disableDrafts,
      initialValue,
      selectedClientId,
      selectedConversation,
      selectedConversation?.conversation_id,
      shouldShowMessengerDraftsUtility,
    ])

    useEffect(() => {
      if (!shouldShowMessengerDraftsUtility && !disableDrafts) {
        inputRef.current = draft
        setInitialValue(draft)
      }
    }, [draft, disableDrafts, shouldShowMessengerDraftsUtility])

    // Declared after the draft effects so a restored unsent message wins over an empty draft.
    const unsentMessage = useSelector(getUnsentMessage)
    useEffect(() => {
      if (!unsentMessage || unsentMessage.conversationId !== selectedConversation?.conversation_id) {
        return
      }
      appDispatch(setUnsentMessage(null))
      // Never drop either text: keep anything typed since the failed send, after the failed message.
      const restored = inputRef.current ? `${unsentMessage.body}\n${inputRef.current}` : unsentMessage.body
      inputRef.current = restored
      setInitialValue(restored)
    }, [appDispatch, selectedConversation?.conversation_id, unsentMessage])

    dblAttachmentRef.current = addedDBLAttachment
    essentialsLinkRef.current = addedEssentialsLink
    savesDraftWithLinksRef.current = messengerDraftsEnabled && Boolean(selectedConversation?.conversation_id)

    const { markMessagesUnread, canUpdateReadState } = useMarkMessagesUnread()

    const canMarkMessagesRead = canUpdateReadState || (isProviderPairConversation && isEmpty(supervisor))
    const markMessagesAsRead = useCallback(async () => {
      if (isProviderPairConversation) {
        if (messagesFetched && newMessageCount > 0 && isEmpty(supervisor) && selectedConversation?.conversation_id) {
          // Clear before the request, as for clients, so messages arriving during it keep their indicator.
          dispatch({ type: 'READ_MESSAGE' })
          try {
            await markProviderPairRead({ conversationId: selectedConversation.conversation_id }).unwrap()
          } catch (e) {
            console.error(`Unable to update unread message - ${e}`)
          }
        }
        return
      }
      if (!canUpdateReadState) {
        return
      }
      const unreadMessageCount = selectedConversation?.conversation_attributes?.unread_provider_messages_count ?? 0
      const unreadAiCoachProviderCount =
        selectedConversation?.conversation_attributes?.unread_ai_coach_messages_provider_count ?? 0
      const hasUnread = unreadMessageCount > 0 || (isAiConversationSelected && unreadAiCoachProviderCount > 0)
      if (hasUnread && isEmpty(supervisor) && selectedConversation?.conversation_id) {
        dispatch({ type: 'READ_MESSAGE' })
        try {
          await updateConversationAttributes({
            conversationId: selectedConversation?.conversation_id,
            isV2: isV2AiCoachSelected,
            unreadProviderMessagesCount: 0,
            ...(isAiConversationSelected && { unreadAiCoachMessagesProviderCount: 0 }),
          }).unwrap()
        } catch (e) {
          console.error(`Unable to update unread message - ${e}`)
        }
      }
    }, [
      canUpdateReadState,
      isAiConversationSelected,
      isV2AiCoachSelected,
      isProviderPairConversation,
      markProviderPairRead,
      messagesFetched,
      newMessageCount,
      selectedConversation?.conversation_attributes?.unread_provider_messages_count,
      selectedConversation?.conversation_attributes?.unread_ai_coach_messages_provider_count,
      selectedConversation?.conversation_id,
      supervisor,
      updateConversationAttributes,
    ])

    // tell the conversation that we are typing
    const onTyping = useCallback(async () => {
      if (!isV2AiCoachSelected && conversationsClient && selectedConversation?.conversation_id) {
        const activeConversation = await conversationsClient.getConversationBySid(selectedConversation?.conversation_id)
        if (conversationsClient.connectionState !== 'connected') {
          return
        }
        activeConversation.typing().catch(() => {})
      }
    }, [conversationsClient, isV2AiCoachSelected, selectedConversation])

    const setInputValue = useCallback(
      (value) => {
        setInputSelection(undefined)
        inputRef.current = value ?? ''
        if (messengerDraftsEnabled && inputRef.current === '' && conversationDraftRef.current?.id) {
          deleteDraft({
            conversationId: conversationDraftRef.current?.conversation_id,
            draftId: conversationDraftRef.current?.id,
          })
        }
      },
      [deleteDraft, messengerDraftsEnabled],
    )

    const setInputValueAndStartTypingIndicator = (value: string) => {
      if (value.trim() === '') {
        insertMessageOptions.current = null
      }
      setInputValue(value)
      onTyping()
    }

    const onReceiverTyping = useCallback(
      (member) => {
        if (isV2AiCoachSelected) {
          return
        }
        // ensure that the typing started event comes from the selected conversation
        if (
          member.conversation.sid === selectedConversation?.conversation_id &&
          member.identity !== conversationsClient?.user.identity
        ) {
          dispatch({ type: 'ON_TYPING', receiverTyping: member.isTyping })
        }
      },
      [conversationsClient?.user.identity, isV2AiCoachSelected, selectedConversation?.conversation_id],
    )

    const onNewMessage = useCallback(
      async (message) => {
        if (
          isV2AiCoachSelected ||
          !selectedConversation?.conversation_id ||
          message.conversation.sid !== selectedConversation?.conversation_id
        ) {
          return
        }

        const isClientMessage = get(message, 'state.attributes.from') !== providerId
        const appendMessage =
          isClientMessage ||
          message.state.attributes.metadata?.conversationInstanceId !== conversationInstanceId.current
        await getMessage({
          conversationId: selectedConversation?.conversation_id,
          messageId: message.sid,
          append: appendMessage,
        })
        if (isClientMessage) {
          dispatch({ type: 'NEW_MESSAGE' })
        }
      },
      [getMessage, isV2AiCoachSelected, providerId, selectedConversation],
    )

    // legacy save draft if the component unmounts - remove when removing shouldShowMessengerDraftsUtility flag
    useEffect(
      () => () => {
        if (shouldShowMessengerDraftsUtility) {
          return
        }
        if (!disableDrafts && selectedConversation?.conversation_id) {
          appDispatch(
            saveMessageDraft({ content: inputRef.current, conversationId: selectedConversation?.conversation_id }),
          )
        }
        // Moving from no conversation to the one this composer just created is not a switch, so keep
        // what was typed since the first send.
        if (selectedConversation?.conversation_id || !createdConversationIdRef.current) {
          inputRef.current = ''
          setInitialValue('')
        }
        // clear live messaging in session if component is unmounted anywhere
        if (inLiveMsgSession) {
          appDispatch(toggleLiveMsgSession(false))
        }
      },
      [
        appDispatch,
        disableDrafts,
        inLiveMsgSession,
        shouldShowMessengerDraftsUtility,
        selectedConversation?.conversation_id,
      ],
    )

    // new draft saving when component unmounts
    useSaveMessageDraft({
      conversationDraftRef,
      dblAttachmentRef,
      essentialsLinkAttachmentRef: essentialsLinkRef,
      essentialsLinkUrlRef,
      inLiveMsgSession,
      inputRef,
      providerId,
      selectedClientId,
      selectedConversation,
      setInitialValue,
      shouldShowMessengerDraftsUtility,
      disableDrafts,
      createdConversationIdRef,
    })

    // Always mark a pair read once on open: cached list counts may be stale. The read also
    // covers messages that arrived while opening history loaded, before the chat's read
    // callback could run. Later messages use that callback, not a list refresh.
    const openedProviderPairRef = useRef<string>()
    // Clear unread messages when component is mounted and messages have already been fetched
    const hasValidSupervisor = useMemo(() => {
      return isEmpty(supervisor) || supervisorsThatCanMarkConversationsAsRead?.includes(supervisor?.id)
    }, [supervisor, supervisorsThatCanMarkConversationsAsRead])

    useEffect(() => {
      if (openedProviderPairRef.current !== selectedConversation?.conversation_id) {
        openedProviderPairRef.current = undefined
      }
      const updateMessage = async () => {
        if (!selectedConversation?.conversation_id) {
          return
        }
        try {
          if (isProviderPairConversation) {
            if (openedProviderPairRef.current === selectedConversation.conversation_id) {
              return
            }
            openedProviderPairRef.current = selectedConversation.conversation_id
            if (newMessageCountRef.current > 0) {
              dispatch({ type: 'READ_MESSAGE' })
            }
            await markProviderPairRead({ conversationId: selectedConversation.conversation_id }).unwrap()
          } else {
            await updateConversationAttributes({
              conversationId: selectedConversation.conversation_id,
              conversationType: selectedConversation.conversation_attributes?.type,
              isV2: isV2AiCoachSelected,
              unreadProviderMessagesCount: 0,
              ...(isAiConversationSelected && { unreadAiCoachMessagesProviderCount: 0 }),
            })
          }
        } catch (e) {
          console.error(`Unable to update unread message - ${e}`)
        }
      }
      if (canMarkMessagesRead && messagesFetched && selectedConversation?.conversation_id && hasValidSupervisor) {
        updateMessage()
      }
    }, [
      canMarkMessagesRead,
      isAiConversationSelected,
      isV2AiCoachSelected,
      isProviderPairConversation,
      markProviderPairRead,
      messagesFetched,
      selectedConversation?.conversation_attributes?.type,
      selectedConversation?.conversation_id,
      hasValidSupervisor,
      updateConversationAttributes,
    ])

    useEffect(() => {
      if (conversationsClient && !isV2AiCoachSelected) {
        conversationsClient.on('messageAdded', onNewMessage)
        conversationsClient.on('typingStarted', onReceiverTyping)
        conversationsClient.on('typingEnded', onReceiverTyping)
      }
      return () => {
        if (conversationsClient && !isV2AiCoachSelected) {
          conversationsClient.off('messageAdded', onNewMessage)
          conversationsClient.off('typingStarted', onReceiverTyping)
          conversationsClient.off('typingEnded', onReceiverTyping)
        }
      }
    }, [conversationsClient, isV2AiCoachSelected, onNewMessage, onReceiverTyping])

    const isProgramBookingLink = (message: string) =>
      message.includes('directPathBooking?directPath=true') &&
      message.includes('directLinkIntent=DIRECT_LINK_CONCURRENT_CARE_REFERRAL') &&
      message.includes('directLinkSource=provider_message')
    const getProgramBookingLinkType = (message: string) => {
      const urlMatch = message.match(/https?:\/\/\S+/)
      if (!urlMatch) return ''
      let url: URL
      try {
        url = new URL(urlMatch[0])
      } catch {
        return ''
      }
      const clientele = url.searchParams.get('clientele') ?? ''
      const treatment = url.searchParams.get('treatment') ?? ''
      const partner = url.searchParams.get('partner') ?? ''
      const offering = url.searchParams.get('offering') ?? ''
      return (
        Programs.getProgramConfigsMatching(
          (c) =>
            !!c.directBookingParams &&
            c.directBookingParams.clientele === clientele &&
            c.directBookingParams.treatment === treatment &&
            c.directBookingParams.partner === partner &&
            c.directBookingParams.offering === offering,
        )[0]?.customerPropertyId ?? ''
      )
    }
    const essentialsLinkUrl = useSelector(getEssentialsArticleUrl)
    essentialsLinkUrlRef.current = essentialsLinkUrl

    const activeConversationId = selectedConversation?.conversation_id
    const activeConversationType = selectedConversation?.conversation_attributes?.type
    const conversationType = isProviderPair ? CONVERSATION_TYPE.PROVIDER_PROVIDER : activeConversationType
    const activeConversationPatientId = selectedConversation?.patient_lyra_id
    const activeConversationCounterpartProviderId = selectedConversation?.counterpartProviderId

    const submitNewMessage = useCallback(
      async (value) => {
        if (sendInFlightRef.current || isReadOnlyRef.current) {
          return
        }
        const existingConversationId = activeConversationId
        // Only for a pre-existing conversation: one we just created targets recipientId already.
        if (
          existingConversationId &&
          (!activeConversationPatientId ||
            !doesConversationMatchRecipient({
              conversationType: activeConversationType,
              conversationPatientId: activeConversationPatientId,
              conversationCounterpartProviderId: activeConversationCounterpartProviderId,
              selectedClientId,
              clientId: client?.id,
              recipientId,
            }))
        ) {
          return
        }
        // Surfaces without ensureConversation (the client-notes side panel) can't create one, so a
        // null id there isn't a failure worth reporting, so stay silent as this path did before.
        if (!existingConversationId && !ensureConversation) {
          return
        }
        if (hasErrorSpanPattern(value)) {
          if (!disableDrafts && existingConversationId) {
            appDispatch(saveMessageDraft({ content: value, conversationId: existingConversationId }))
          }
          setInitialValue(value)
          setShowContainsErrorModal(true)
          return
        }
        inputRef.current = ''
        setInitialValue('')
        sendInFlightRef.current = true
        let conversationId: string | null
        try {
          conversationId = existingConversationId ?? (await ensureConversation?.()) ?? null
        } finally {
          sendInFlightRef.current = false
        }
        if (!conversationId) {
          setInitialValue(value)
          inputRef.current = value
          appDispatch(
            setToastContent({
              id: 'send-message-no-conversation-error',
              text: formatMessage({
                defaultMessage: 'Unable to send message. Please try again.',
                description: 'Error toast shown when the conversation needed to send a message could not be created',
              }),
              toastType: 'error',
            }),
          )
          return
        }
        if (!existingConversationId) {
          createdConversationIdRef.current = conversationId
        }
        const metadata: Record<string, any> = {
          conversationInstanceId: conversationInstanceId.current,
          ...(conversationType === CONVERSATION_TYPE.PROVIDER_PROVIDER &&
            coveringProviderActorName && { covering_provider_actor_name: coveringProviderActorName }),
        }
        if (addedDBLAttachment) {
          metadata.attachments = [
            ...(metadata.attachments ?? []),
            {
              key: uuidv4(),
              type: MessageAttachmentType.DBL,
            },
          ]
        }
        if (addedEssentialsLink && insertedEssentialsLinkUrl) {
          const essentialsContentPath = insertedEssentialsLinkUrl.startsWith(ESSENTIALS_BASE_URL)
            ? insertedEssentialsLinkUrl.slice(ESSENTIALS_BASE_URL.length)
            : insertedEssentialsLinkUrl
          metadata.attachments = [
            ...(metadata.attachments ?? []),
            {
              key: uuidv4(),
              type: MessageAttachmentType.ESSENTIALS_LINK,
              url: essentialsContentPath,
            },
          ]
        }
        if (isProgramBookingLink(value)) {
          metadata.attachments = [
            ...(metadata.attachments ?? []),
            {
              key: uuidv4(),
              type: MessageAttachmentType.PBL,
              program: getProgramBookingLinkType(value),
            },
          ]
        }
        const episodeId = currentEpisode?.id ?? episodes?.find((e) => e.state === EpisodeStates.IN_PROGRESS)?.id
        if (attachedDocument && !episodeId) {
          appDispatch(
            setToastContent({
              id: 'document-send-no-episode-error',
              text: formatMessage({
                defaultMessage: 'Unable to send document: no active episode found.',
                description: 'Error toast shown when a document is attached but no active episode can be resolved',
              }),
              toastType: 'error',
            }),
          )
          return
        }
        if (attachedDocument && episodeId && client?.id) {
          try {
            const createResult = await createDocumentFromTemplate({
              documentTemplateId: attachedDocument.document_template_id,
              episodeId,
              isShared: true,
              providerId,
            }).unwrap()
            const shareResult = await shareDocument({
              documentId: createResult.document_id,
              recipientId: client.id,
              providerId,
            }).unwrap()
            metadata.attachments = [
              ...(metadata.attachments ?? []),
              {
                key: uuidv4(),
                type: MessageAttachmentType.DOCUMENT,
                documentId: createResult.document_id,
                fileName: attachedDocument.file_name,
                ...(attachedDocument.signing_flow && { signingFlow: attachedDocument.signing_flow }),
                ...(shareResult.signing_url && { signingUrl: shareResult.signing_url }),
              },
            ]
          } catch (error) {
            if (isFileNameAlreadyExistsError(error)) {
              pendingMessageValue.current = value
              setShowDocumentDuplicateModal(true)
            } else {
              pendingMessageValue.current = value
              setShowDocumentGenericErrorModal(true)
            }
            return
          }
        }
        // if rich text editor was used, we strip out html
        let stripped = value.replaceAll('<p></p>', '\n')
        stripped = stripped.replaceAll('</p>', '\n')
        stripped = stripped.replaceAll('<p>', '')
        stripped = convertHtmlSpace(parseHtmlTags(stripped))
        if (!insertMessageOptions.current?.attributesToAttach && savedTemplateId) {
          insertMessageOptions.current = {
            ...insertMessageOptions.current,
            attributesToAttach: {
              message_template_id: savedTemplateId,
            },
          }
        }
        if (insertMessageOptions.current?.attributesToAttach?.message_template_id) {
          metadata.message_template_id = insertMessageOptions.current?.attributesToAttach?.message_template_id
        }
        const sendResult = createMessage({
          conversationId,
          conversationType,
          data: {
            message_body: stripped,
            message_type: 'direct',
            message_author_id: providerId,
            message_author_type: MESSAGE_AUTHOR_TYPE.PROVIDER,
            metadata,
            ...insertMessageOptions.current?.attributesToAttach,
            ...(conversationDraftRef.current?.id && { message_draft_id: conversationDraftRef.current?.id }),
          },
        })
        // This send created the conversation, so the optimistic write had no cache entry to land in.
        const createdConversationId = existingConversationId ? null : conversationId
        sendResult.unwrap().then(
          () => {
            // Fetch after the message persists, since fetching sooner caches a list without it.
            if (createdConversationId) {
              appDispatch(
                messagesApi.util.prefetch('getMessages', { conversationId: createdConversationId }, { force: true }),
              )
            }
          },
          (error: unknown) => {
            // sendMessage surfaces the failure. Without a cache entry, or once the backend replaced the
            // conversation, there is no failed bubble to retry from, so hand the text to whichever
            // composer shows that conversation. Promotion can remount this one.
            const recoveryConversationId = getReplacementConversationId(error) ?? createdConversationId
            if (recoveryConversationId) {
              appDispatch(setUnsentMessage({ conversationId: recoveryConversationId, body: value }))
            }
          },
        )
        if (messengerDraftsEnabled && conversationDraftRef.current?.id) {
          deleteDraft({
            conversationId,
            draftId: conversationDraftRef.current?.id,
          })
        }
        conversationDraftRef.current = undefined
        setInputSelection(undefined)
        if (!disableDrafts) {
          appDispatch(saveMessageDraft({ content: '', conversationId }))
        }
        clearComposerAttachments()
        track({ event: 'SEND_MESSAGE', details: { program: selectedEpisodeProgram } })
        setSavedTemplateId && setSavedTemplateId(null)
        onMessageSent?.()
        insertMessageOptions.current?.onSend?.()
        insertMessageOptions.current = null
      },
      [
        activeConversationCounterpartProviderId,
        activeConversationId,
        activeConversationPatientId,
        activeConversationType,
        conversationType,
        client?.id,
        selectedClientId,
        recipientId,
        ensureConversation,
        addedDBLAttachment,
        addedEssentialsLink,
        savedTemplateId,
        createMessage,
        coveringProviderActorName,
        providerId,
        messengerDraftsEnabled,
        disableDrafts,
        appDispatch,
        clearComposerAttachments,
        selectedEpisodeProgram,
        setSavedTemplateId,
        onMessageSent,
        insertedEssentialsLinkUrl,
        deleteDraft,
        attachedDocument,
        createDocumentFromTemplate,
        shareDocument,
        currentEpisode,
        episodes,
        formatMessage,
      ],
    )

    const navigate = useNavigate()

    const handleInboxIconPress = useCallback(() => {
      if (!closePopover || !client?.id) {
        return
      }

      // Set selected client in Redux so it persists when navigating to messenger
      appDispatch(setSelectedClientId(client.id))
      appDispatch(setLastConversationClientId(client.id))

      // Navigate to messenger page (will restore client from Redux)
      navigate('/messages')
    }, [closePopover, client?.id, appDispatch, navigate])

    const handleDocumentAttachmentPressed = useCallback(
      (attachment: MessageAttachmentData) => {
        if (attachment.type === MessageAttachmentType.DOCUMENT) {
          navigate(CLIENT_DOCUMENTS.route)
          closePopover?.()
        }
      },
      [navigate, closePopover],
    )

    const handleLinkClickNew = useCallback(
      (_: string, __: boolean, activityInfo?: ActivityReviewInfo) => {
        if (!activityInfo) {
          return
        }
        const { id, responseId, activityType } = activityInfo
        switch (activityType) {
          case 'feedback':
            reduxDispatch(clearSelectedAssignmentId())
            navigate(CLIENTS_ASSIGNMENT_DETAILS.route, {
              state: { assignmentId: id, responseId: responseId },
            })
            break
          case 'session_cancellation':
          case 'request_session_time':
            navigate(CLIENTS_SESSIONS.route)
            break
        }
        closePopover?.()
      },
      [closePopover, navigate, reduxDispatch],
    )

    const canAttachDBL = useMemo(
      () => canSeeDirectBookingLink({ provider: provider as unknown as ProviderInfo, episodes, appointments }),
      [appointments, episodes, provider],
    )

    const attachDBLDisabled = !canAttachDBL || addedDBLAttachment

    const getMessageType = (attributes: MessageAttributes) => {
      const linkText = attributes?.metadata?.linkText
      const activityResponseId = attributes?.metadata?.activity_response_id
      const activityId = attributes?.metadata?.activity_id
      const isDeletedMessage =
        !isNil(attributes?.retention_status) && attributes?.retention_status === 'DELETED_LIVE_MESSAGE'
      const isActivity = Boolean(linkText) || (Boolean(activityResponseId) && Boolean(activityId))

      if (isDeletedMessage) {
        return MessageType.DELETED
      } else if (isActivity) {
        return MessageType.ACTIVITY
      } else {
        return MessageType.CHAT_BUBBLE
      }
    }

    const memoizedMessages = useMemo(() => {
      if (!messages) return []
      return parseAllowedMessages(messages).map((m, index) => {
        const attributes = JSON.parse(m?.attributes)
        const isReceiver = isMessageFromRecipient(
          { authorType: attributes.author_type, authorId: attributes.from },
          providerId,
          selectedConversation?.conversation_attributes?.type,
        )
        const essentials = attributes.metadata?.essentials
        const attachedResources = attributes.metadata?.attached_resources as LyraLibraryResource[] | undefined
        const parsedEssentials = parseEssentialsMetadata(attachedResources, essentials)
        const previousMessage = messages[index + 1]
        let showTime = true
        const dateCreated = new Date(m.date_created)
        // we only want to show the time stamp if 10 minutes have passed
        showTime =
          index === messages.length - 1
            ? true
            : isAfter(sub(dateCreated, { minutes: 10 }), new Date(previousMessage?.date_created))
        const genAiMessageId = attributes.metadata?.gen_ai_message_id
        const messageFeedback = aiCoachFeedback?.find((fb: Feedback) => fb.entity_id === genAiMessageId)
        const messageFeedbackValue = messageFeedback?.feedback_response?.[0]?.answer
        const feedbackResponseId = messageFeedback?.feedback_response?.[0]?.id
        const riskLevel = attributes.metadata?.risk_level
        const authorAttribution = getMessageAuthorAttribution(intl, attributes, isSupervisorMessageAttributionEnabled)
        return {
          authorType: attributes.author_type,
          messageId: m.message_id,
          receiver: isReceiver,
          dateCreated: m?.date_created,
          message: m.body,
          isLatestMessage: index === messages.length - 1,
          // if the message has no status, it has been sent
          sendStatus: m.sendStatus === undefined ? 'sent' : m.sendStatus,
          showTime,
          linkText: attributes.metadata?.linkText,
          activityResponseId: attributes.metadata?.activity_response_id,
          activityId: attributes.metadata?.activity_id,
          submitDate: attributes.metadata?.submit_date,
          activityTitle: attributes.metadata?.title,
          timeZone: (provider as any).time_zone,
          type: getMessageType(attributes),
          messageType: attributes.message_type,
          showAvatar: false,
          attachments: attributes.metadata?.attachments,
          essentials: parsedEssentials,
          genAiMessageId,
          messageFeedbackValue,
          feedbackResponseId,
          riskLevel,
          authorAttribution,
        }
      })
    }, [
      messages,
      provider,
      aiCoachFeedback,
      intl,
      providerId,
      selectedConversation?.conversation_attributes?.type,
      isSupervisorMessageAttributionEnabled,
    ])

    const onAiCoachFeedbackPressed = useCallback(
      async ({
        value,
        genAiMessageId,
        feedbackResponseId,
      }: {
        value: string | null
        genAiMessageId: string
        feedbackResponseId?: string
      }) => {
        // The feedback buttons disable themselves while this promise is pending
        // (see MessageFeedback), so the await is what keeps them disabled until the
        // request resolves to prevent rapid clicking which causes errors.
        // Errors are hidden entirely since this is fire and forget.
        try {
          if (value == null) {
            const existing = aiCoachFeedback?.find(
              (fb: Feedback) => fb.entity_id === genAiMessageId && fb.user_type === 'provider',
            )
            if (existing?.id != null) {
              await deleteFeedbackMutation({ feedbackId: existing.id })
            }
            return
          }

          if (feedbackResponseId) {
            await updateFeedbackResponseMutation({ feedbackResponseId, answer: value })
          } else {
            await createFeedback({
              feedback: {
                entity_type: FeedbackEntityType.AI_COACH,
                entity_id: genAiMessageId,
                lyra_id: providerId,
                user_type: 'provider',
                feedback_response: [{ question: 'ai_coach_response_thumbs_feedback', answer: value }],
              },
            })
          }
        } catch (error) {
          console.error('AI Coach feedback request failed', error)
        }
      },
      [aiCoachFeedback, createFeedback, deleteFeedbackMutation, updateFeedbackResponseMutation, providerId],
    )

    const handleAddDBL = useCallback(() => {
      setAddedDBLAttachment(true)
    }, [])

    const handleDBLClose = useCallback(() => {
      setAddedDBLAttachment(false)
    }, [])

    const handleRemoveEssentialsLink = useCallback(() => {
      appDispatch(setEssentialsArticleUrl(null))
      appDispatch(setInsertEssentialsArticleForClientId(null))
      setAddedEssentialsLink(false)
      setInsertedEssentialsLinkUrl(null)
    }, [appDispatch])

    const handleDocumentDuplicateClose = useCallback(() => {
      setShowDocumentDuplicateModal(false)
      inputRef.current = pendingMessageValue.current
      setInitialValue(pendingMessageValue.current)
    }, [])

    const handleGoToDocuments = useCallback(() => {
      setShowDocumentDuplicateModal(false)
      navigate(CLIENT_DOCUMENTS.route)
      closePopover?.()
    }, [navigate, closePopover])

    const handleDocumentErrorClose = useCallback(() => {
      setShowDocumentGenericErrorModal(false)
    }, [])

    const handleDocumentErrorTryAgain = useCallback(() => {
      setShowDocumentGenericErrorModal(false)
      submitNewMessage(pendingMessageValue.current)
    }, [submitNewMessage])

    const clientName = useMemo(
      () =>
        client
          ? getClientFullName(client, isPreferredNameEnabled)
          : formatMessage({
              defaultMessage: 'this client',
              description: 'Fallback name for client in document error modal',
            }),
      [client, isPreferredNameEnabled, formatMessage],
    )

    const inputActions = useMemo(() => {
      const showLCxMenu = !isDAEngageProvider && onOpenDocumentLibrary !== undefined && isDocumentMessageEnabled
      const showDAMenu = isDAEngageProvider && onOpenDocumentLibrary !== undefined && isDocumentMessageEnabled

      const directBookingLinkInline = (
        <DirectBookingLinkInline
          key='DBLInline'
          onPress={handleAddDBL}
          disabled={attachDBLDisabled}
          disabledTooltipContent={
            !canAttachDBL
              ? formatMessage({
                  defaultMessage: 'Unable to send direct booking link to this client',
                  description: 'Tooltip on DBL attachment button when provider cannot add attachment',
                })
              : formatMessage({
                  defaultMessage: 'A direct booking link is already inserted into the message',
                  description: 'Tooltip on DBL attachment button when attachment already added',
                })
          }
        />
      )

      if (showLCxMenu || showDAMenu) {
        const shareDocumentItem = (
          <QuickActionsMenuItem
            key='share-document'
            onPress={() => {
              setIsPlusMenuOpen(false)
              onOpenDocumentLibrary!()
            }}
          >
            <SideNavDocumentIcon size={24} fillColor={colors.iconDefault} />
            <BodyText
              text={formatMessage({
                defaultMessage: 'Share document',
                description: 'Menu item to share a document from the document library in the chat input',
              })}
            />
          </QuickActionsMenuItem>
        )

        const quickActionsMenuContent = !isDAEngageProvider ? (
          <>
            {showTemplatesButtonInMessageCenter && onOpenSideBarItem !== undefined && (
              <QuickActionsMenuItem
                onPress={() => {
                  setIsPlusMenuOpen(false)
                  onOpenSideBarItem(ChatSideBarSelection.TEMPLATES)
                }}
              >
                <RulerAndPencilIconFilled size={24} fillColor={colors.iconDefault} />
                <BodyText
                  text={formatMessage({
                    defaultMessage: 'Insert message templates',
                    description: 'Menu item to open the message templates sidebar in the chat input',
                  })}
                />
              </QuickActionsMenuItem>
            )}
            {(shouldShowMessengerAIDrafts || shouldShowPostSessionAIDrafts) && onOpenSideBarItem !== undefined && (
              <QuickActionsMenuItem
                onPress={() => {
                  setIsPlusMenuOpen(false)
                  onOpenSideBarItem(ChatSideBarSelection.SMART_SUGGESTIONS)
                }}
              >
                <PencilSparkleIcon size={24} fillColor={colors.iconDefault} />
                <BodyText
                  text={formatMessage({
                    defaultMessage: 'Insert smart suggestions',
                    description: 'Menu item to open the smart suggestions sidebar in the chat input',
                  })}
                />
              </QuickActionsMenuItem>
            )}
            {shareDocumentItem}
          </>
        ) : (
          <>
            {showWellnessCenterEssentials && !isEssentialsDisabled && onOpenSideBarItem !== undefined && (
              <QuickActionsMenuItem
                onPress={() => {
                  setIsPlusMenuOpen(false)
                  onOpenSideBarItem(ChatSideBarSelection.WELLNESS_LIBRARY_ESSENTIALS)
                }}
              >
                <ResourcesIcon size={24} fillColor={colors.iconDefault} />
                <BodyText
                  text={formatMessage({
                    defaultMessage: 'Share Wellness Library content',
                    description: 'Menu item to open the wellness library sidebar in the chat input',
                  })}
                />
              </QuickActionsMenuItem>
            )}
            {shareDocumentItem}
          </>
        )

        return [
          directBookingLinkInline,
          <InfoPopover
            key='plus-menu'
            placement='top left'
            popoverTriggerAction='press'
            shouldDisplayCloseButton={false}
            isPopoverOpen={isPlusMenuOpen}
            setIsPopoverOpen={setIsPlusMenuOpen}
            unstyledWrapper
            popoverStyle={{}}
            trigger={
              <Pressable
                testID={tID('BaseChat-quickActionsMenu')}
                accessibilityRole='button'
                accessibilityLabel={formatMessage({
                  defaultMessage: 'Quick actions',
                  description: 'Accessibility label for plus menu button in chat input',
                })}
                style={{ alignSelf: 'flex-start', borderRadius: 36, alignItems: 'center', padding: 8 }}
              >
                <PlusSignIcon size={20} fillColor={colors.iconDefault} />
              </Pressable>
            }
            content={quickActionsMenuContent}
          />,
        ]
      }
      return [directBookingLinkInline]
    }, [
      attachDBLDisabled,
      canAttachDBL,
      colors.iconDefault,
      formatMessage,
      handleAddDBL,
      isDAEngageProvider,
      isDocumentMessageEnabled,
      isEssentialsDisabled,
      isPlusMenuOpen,
      onOpenDocumentLibrary,
      onOpenSideBarItem,
      shouldShowMessengerAIDrafts,
      shouldShowPostSessionAIDrafts,
      showTemplatesButtonInMessageCenter,
      showWellnessCenterEssentials,
    ])
    const inputAttachments = useMemo(
      () => [
        ...(addedDBLAttachment
          ? [
              <DirectBookingLinkMessageAttachment
                key='DBLAttachment'
                onPress={noop}
                onClosePress={handleDBLClose}
                showClose
              />,
            ]
          : []),
        ...(addedEssentialsLink && insertedEssentialsLinkUrl
          ? [
              <EssentialsLinkMessageAttachment
                key='essentialsLinkAttachment'
                onPress={noop}
                onClosePress={handleRemoveEssentialsLink}
                showClose
                text={insertedEssentialsLinkUrl}
              />,
            ]
          : []),
      ],
      [addedDBLAttachment, addedEssentialsLink, insertedEssentialsLinkUrl, handleDBLClose, handleRemoveEssentialsLink],
    )

    const canSendMessages = (props.receiverMsgable ?? !isAiConversationSelected) && !props.disableSend
    const inputTopAttachments = useMemo(
      () => [
        ...(conversationType === CONVERSATION_TYPE.PROVIDER_PROVIDER &&
        isSupervisorMessageAttributionEnabled &&
        coveringProviderActorName &&
        canSendMessages
          ? [
              <SupervisorMessageAttributionNotice
                key='supervisor-message-attribution'
                actorName={coveringProviderActorName}
              />,
            ]
          : []),
        ...(composerNotice ? [<React.Fragment key='composerNotice'>{composerNotice}</React.Fragment>] : []),
        ...(attachedDocument
          ? [
              <DocumentAttachmentRow key='documentAttachment'>
                <SideNavDocumentIcon fillColor={colors.iconDefault} size={24} />
                <DocumentAttachmentFileName text={attachedDocument.file_name} />
                <PressableOpacity
                  onPress={() => setAttachedDocument(null)}
                  accessibilityLabel={formatMessage({
                    defaultMessage: 'Remove document',
                    description: 'Accessibility label to remove document attachment',
                  })}
                >
                  <CloseCircleIcon size={20} fillColor={colors.iconDefault} />
                </PressableOpacity>
              </DocumentAttachmentRow>,
            ]
          : []),
      ],
      [
        attachedDocument,
        canSendMessages,
        colors.iconDefault,
        composerNotice,
        conversationType,
        coveringProviderActorName,
        formatMessage,
        isSupervisorMessageAttributionEnabled,
      ],
    )

    const scrollInfo = useMemo(() => {
      return { scrollToEnd: false, animatedScroll: false }
    }, [])

    const handleRequestCloseConfirmationmodal = useCallback(() => {
      setShowContainsErrorModal(false)
    }, [])

    const isMinor = isDateOfBirthUnder18(client?.date_of_birth ?? '')

    if (showContainsErrorModal) {
      inputRef.current = initialValue
      chatRef.current?.insertMessage(initialValue)
      saveMessageDraft({ content: initialValue, conversationId: selectedConversation?.conversation_id ?? '' })
      return (
        <ConfirmationModal
          visible={showContainsErrorModal}
          showCancel={false}
          onRequestClose={handleRequestCloseConfirmationmodal}
          onConfirmationButtonPress={handleRequestCloseConfirmationmodal}
          modalTitle='Review your message for errors'
          modalContents={`We noticed the word "ERROR" in your message. This could mean that some template information needs to be updated. Please review your message to ensure it's personalized and error-free.`}
          confirmationButtonText='Got it'
        />
      )
    }

    return (
      <>
        <Chat
          ref={handleChatRef}
          isAiCoachSelected={isAiConversationSelected}
          isProviderAiCoachView={isAiConversationSelected}
          onAiCoachFeedbackPressed={isAiConversationSelected ? onAiCoachFeedbackPressed : undefined}
          showFeedbackOnHoverLeft={isAiConversationSelected}
          chatHeaderTitle=''
          liveSessionBadge=''
          displayName={recipientName ?? (client ? getClientFullName(client, isPreferredNameEnabled) : '')}
          HeaderTitleBadge={
            closePopover ? (
              <InboxIconContainer
                onPress={handleInboxIconPress}
                accessibilityLabel={formatMessage({
                  defaultMessage: 'Open messaging page',
                  description: 'Accessibility label for inbox icon button in chat popover header',
                })}
                testID={tID('InboxIconContainer')}
              >
                <InboxIcon size={18} fillColor={colors.iconDefault} />
              </InboxIconContainer>
            ) : undefined
          }
          onBack={closePopover}
          onSendMessage={submitNewMessage}
          onInputChange={setInputValueAndStartTypingIndicator}
          messages={memoizedMessages}
          receiverTyping={receiverTyping}
          showAiChatConsent={false}
          receiverMsgable={!isAiConversationSelected}
          unreadMsgCount={canMarkMessagesRead ? newMessageCount : 0}
          scrollInfo={scrollInfo}
          showNewMsgIndicator={canMarkMessagesRead && newMessageCount > 0}
          onNewMsgPressed={canMarkMessagesRead ? markMessagesAsRead : undefined}
          onChatBubblePressed={handleLinkClickNew}
          onAttachmentPressed={handleDocumentAttachmentPressed}
          closeStyle={CloseStyles.BackArrow}
          showChevron={true}
          inputValue={initialValue}
          conversationDateCreated={selectedConversation?.conversation_date_created}
          isLoading={loadingMessages || isLoading}
          showClose={false}
          markMessagesAsUnread={canUpdateReadState ? markMessagesUnread : undefined}
          showMenu={
            canUpdateReadState &&
            !isSupervisor &&
            hasRecipientMessages(
              { conversation_messages: messages, next_page_token: null },
              providerId,
              selectedConversation?.conversation_attributes?.type,
            )
          }
          shouldInvert={true}
          inputActions={showInputActions ? inputActions : undefined}
          useActionsLayout
          inputAttachments={inputAttachments}
          inputTopAttachments={inputTopAttachments}
          inputSelection={inputSelection}
          onReachedMessagesEnd={hasMoreMessages ? loadMoreMessages : undefined}
          isLoadingNextPage={isLoadingNextPage}
          showProviderUnavailableMessage={false}
          hasGuardian={isGuardianForMinor}
          guardianName={isGuardianForMinor ? guardian.guardianName : undefined}
          patientFullName={
            isGuardianForMinor ? (client ? getClientFullName(client, isPreferredNameEnabled) : '') : undefined
          }
          isDAEngageProvider={isDAEngageProvider}
          isMinor={isMinor}
          {...props}
        />
        {showDocumentDuplicateModal && (
          <ConfirmationModal
            visible={showDocumentDuplicateModal}
            onRequestClose={handleDocumentDuplicateClose}
            modalTitle={formatMessage({
              defaultMessage: 'Document already exists',
              description: 'Title for modal shown when a duplicate document is shared via message',
            })}
            modalContents={formatMessage({
              defaultMessage:
                'This document has already been uploaded or shared with this client. Go to Documents to verify its status.',
              description: 'Body text for duplicate document error modal',
            })}
            confirmationButtonText={formatMessage({
              defaultMessage: 'Go to document',
              description: 'Button label to navigate to the documents page from the duplicate document modal',
            })}
            onConfirmationButtonPress={handleGoToDocuments}
            showCancel
            cancelButtonText={formatMessage({
              defaultMessage: 'Return to message',
              description: 'Button label to dismiss the duplicate document modal and return to composing',
            })}
            onCancelPressed={handleDocumentDuplicateClose}
            testID={tID('BaseChat-documentDuplicateModal')}
            singleLineButtons
          />
        )}
        {showDocumentGenericErrorModal && (
          <ConfirmationModal
            visible={showDocumentGenericErrorModal}
            onRequestClose={handleDocumentErrorClose}
            modalTitle={formatMessage({
              defaultMessage: 'Something went wrong',
              description: 'Title for modal shown when document sharing fails with an unexpected error',
            })}
            modalContents={formatMessage(
              {
                defaultMessage: "We couldn't send this document to {clientName}. Please try sending it again.",
                description: 'Body text for generic document sharing error modal',
              },
              { clientName },
            )}
            confirmationButtonText={formatMessage({
              defaultMessage: 'Try again',
              description: 'Button label to retry sending the document after a generic error',
            })}
            onConfirmationButtonPress={handleDocumentErrorTryAgain}
            showCancel
            cancelButtonText={formatMessage({
              defaultMessage: 'Cancel',
              description: 'Button label to dismiss the generic document error modal',
            })}
            testID={tID('BaseChat-documentErrorModal')}
            singleLineButtons
          />
        )}
      </>
    )
  },
)

const mapStateToProps = (state: RootState) => {
  const selectedConversationId = getSelectedConversationId(state)
  const draft = getMessengerDrafts(state).find((draft) => draft.conversationId === selectedConversationId)
  return {
    conversationsClient: getConversationsClient(state),
    draft: draft?.content ?? '',
    inLiveMsgSession: inLiveMsgSession(state),
    selectedClientId: getSelectedClientId(state),
  }
}

const mapDispatchToProps = (dispatch: any) => ({
  actions: bindActionCreators(
    {
      setToastContent,
    },
    dispatch,
  ),
})

const connector = connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })

export default connector(BaseChat)
